import { publicApiService, PUBLIC_ENDPOINTS, apiService, AUTH_ENDPOINTS, authApiService, getNormalizedApiBaseUrl } from '../config/api';
import { uploadSingleFile, FOLDER_TYPES } from './fileUploadService';

// Public API service (no authentication required)
export const publicApi = {
  // Get all roles from real backend (public endpoint)
  getRoles: async (language = null) => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.ALL_ROLES, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      throw error;
    }
  },

  // Get roles by user type (public endpoint)
  getRolesByUserType: async (userType) => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.ROLES_BY_USER_TYPE(userType));
      return response;
    } catch (error) {
      console.error('Failed to fetch roles by user type:', error);
      throw error;
    }
  },

  // Login user (public endpoint)
  login: async (credentials, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.LOGIN, credentials, language);
      return response;
    } catch (error) {
      console.error('Failed to login:', error);
      throw error;
    }
  },

  // Get services offered (public endpoint)
  getServicesOffered: async (language = null) => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.SERVICES_OFFERED, language);

      // Transform the response to match the expected structure
      // New API returns result.services with 'children' instead of 'subServices'
      // Also maps 'url' field to various image field names for backward compatibility
      if (response?.result?.services) {
        const transformedServices = response.result.services.map(service => {
          // Transform children to subServices and preserve url field
          const transformedSubServices = (service.children || []).map(child => ({
            ...child,
            // Map url to various image field names for backward compatibility
            url: child.url,
            imageUrl: child.url,
            image: child.url,
            thumbnailUrl: child.url,
            iconUrl: child.url
          }));

          return {
            ...service,
            subServices: transformedSubServices, // Map children to subServices for backward compatibility
            // Map url to various image field names for backward compatibility
            url: service.url,
            imageUrl: service.url,
            image: service.url,
            thumbnailUrl: service.url,
            iconUrl: service.url,
            profileImageUrl: service.url,
            // Keep children for future use if needed
            children: service.children || []
          };
        });

        return {
          ...response,
          result: {
            ...response.result,
            services: transformedServices
          }
        };
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch services offered:', error);
      throw error;
    }
  },

  // Get locations (public endpoint)
  getLocations: async (language = null) => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.LOCATIONS, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      throw error;
    }
  },

  // Get materials hierarchy (public - for supplier signup)
  getMaterialHierarchy: async (language = null) => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.MATERIALS_HIERARCHY, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch materials hierarchy:', error);
      throw error;
    }
  },

  // Get engineer services (public - for engineer signup, Inspection service offering)
  getEngineerServices: async (language = null) => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.ENGINEER_SERVICES, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch engineer services:', error);
      throw error;
    }
  },

  // Get main contractor services (public endpoint)
  getMainContractorServices: async (language = null, level = null) => {
    try {
      let endpoint = PUBLIC_ENDPOINTS.MAIN_CONTRACTOR_SERVICES;
      if (level !== null && level !== undefined) {
        endpoint += `?level=${level}`;
      }
      const response = await publicApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch main contractor services:', error);
      throw error;
    }
  },

  // Get services (public endpoint)
  getServices: async () => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.SERVICES_OFFERED);
      return response;
    } catch (error) {
      console.error('Failed to fetch services:', error);
      throw error;
    }
  },

  // Send Mobile OTP (public endpoint)
  sendMobileOtp: async (phoneNumber, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.SEND_MOBILE_OTP(phoneNumber), {}, language);
      return response;
    } catch (error) {
      console.error('Failed to send mobile OTP:', error);
      throw error;
    }
  },

  // Send Email OTP (public endpoint)
  sendEmailOtp: async (email, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.SEND_EMAIL_OTP(email), {}, language);
      return response;
    } catch (error) {
      console.error('Failed to send email OTP:', error);
      throw error;
    }
  },

  // Forgot Password (public endpoint) - POST request with email as query parameter
  forgotPassword: async (email, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.REQUEST_FORGOT_PASSWORD(email), {}, language);
      return response;
    } catch (error) {
      console.error('Failed to send forgot password email:', error);
      throw error;
    }
  },

  // Reset Password (public endpoint)
  resetPassword: async (token, email, password, confirmPassword, language = null) => {
    try {
      const payload = { token, email, password, confirmPassword };
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.RESET_PASSWORD, payload, language);
      return response;
    } catch (error) {
      console.error('Failed to reset password:', error);
      throw error;
    }
  },

  // Verify Email OTP (public endpoint)
  verifyEmailOtp: async (email, otp, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.VERIFY_EMAIL_OTP, { email, otp }, language);
      return response;
    } catch (error) {
      console.error('Failed to verify email OTP:', error);
      throw error;
    }
  },

  // Secure Verify Email OTP (public endpoint) - new endpoint
  secureVerifyEmailOtp: async (key, otp, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.SECURE_VERIFY_EMAIL_OTP, { key, otp }, language);
      return response;
    } catch (error) {
      console.error('Failed to verify email OTP:', error);
      throw error;
    }
  },

  // Verify Mobile OTP (public endpoint)
  verifyMobileOtp: async (phoneNumber, otp, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.VERIFY_MOBILE_OTP, { phoneNumber, code: otp }, language);
      return response;
    } catch (error) {
      console.error('Failed to verify mobile OTP:', error);
      throw error;
    }
  },

  // Secure Verify Mobile OTP (public endpoint) - new endpoint
  secureVerifyMobileOtp: async (key, otp, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.SECURE_VERIFY_MOBILE_OTP, { key, otp }, language);
      return response;
    } catch (error) {
      console.error('Failed to verify mobile OTP:', error);
      throw error;
    }
  },

  // Check email and phone availability (public endpoint) - before sending OTP
  checkEmailPhoneAvailability: async ({ email, phoneNumber }, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.CHECK_EMAIL_PHONE_AVAILABILITY, { email, phoneNumber }, language);
      return response;
    } catch (error) {
      console.error('Failed to check email/phone availability:', error);
      throw error;
    }
  },

  // Register Provider (public endpoint) - new endpoint
  registerProvider: async (userData, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.REGISTER_PROVIDER, userData, language);
      return response;
    } catch (error) {
      console.error('Failed to register provider:', error);
      throw error;
    }
  },

  // Sequential verification: Mobile OTP first, then Email OTP only if mobile succeeds
  verifyOtpSequentially: async (phoneNumber, mobileOtp, email, emailOtp) => {
    try {
      // Step 1: Verify Mobile OTP
      const mobileResponse = await publicApi.verifyMobileOtp(phoneNumber, mobileOtp);

      // Check if mobile verification was successful (200 response)
      if (mobileResponse && (mobileResponse.success || mobileResponse.status === 200)) {
        // Step 2: Verify Email OTP only if mobile succeeded
        const emailResponse = await publicApi.verifyEmailOtp(email, emailOtp);

        return {
          success: true,
          mobileVerification: mobileResponse,
          emailVerification: emailResponse,
          message: 'Both mobile and email OTP verification completed successfully'
        };
      } else {
        return {
          success: false,
          mobileVerification: mobileResponse,
          emailVerification: null,
          message: 'Mobile OTP verification failed, email OTP verification skipped'
        };
      }
    } catch (error) {
      console.error('Sequential OTP verification failed:', error);
      throw error;
    }
  },

  // Update service provider license (public endpoint - users with expired licenses may not have tokens)
  updateServiceProviderLicense: async (licenseData, language = null) => {
    try {
      const response = await publicApiService.post(PUBLIC_ENDPOINTS.UPDATE_SERVICE_PROVIDER_LICENSE, licenseData, language);
      return response;
    } catch (error) {
      console.error('Failed to update service provider license:', error);
      throw error;
    }
  },
};


export const authenticatedApi = {
  // Get access token from localStorage
  getAccessToken: () => {
    return localStorage.getItem('access_token');
  },

  // Update user profile assets
  updateUserProfileAssets: async (payload, accessToken = null) => {
    try {
      let response;

      if (accessToken) {
        // Use custom API call with provided token (for registration flow)
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        };

        // Add Accept-Language header if user is logged in
        const userData = sessionStorage.getItem('inchaa_user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user.preferredLanguage) {
              headers['Accept-Language'] = user.preferredLanguage;
            }
          } catch (error) {
            console.error('Error parsing user data for language header:', error);
          }
        }

        const API_BASE_URL = getNormalizedApiBaseUrl();
        const url = `${API_BASE_URL}${AUTH_ENDPOINTS.UPDATE_USER_PROFILE_ASSETS}`;
        const fetchResponse = await fetch(url, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(payload)
        });
        if (!fetchResponse.ok) {
          const errorData = await fetchResponse.json().catch(() => ({ message: `HTTP error! status: ${fetchResponse.status}` }));
          console.error('❌ API error response:', errorData);
          throw new Error(errorData.message || `HTTP error! status: ${fetchResponse.status}`);
        }

        response = await fetchResponse.json();
      } else {
        // Use default auth API service (uses session storage token)
        response = await authApiService.post(AUTH_ENDPOINTS.UPDATE_USER_PROFILE_ASSETS, payload);
      }

      return response;
    } catch (error) {
      console.error('❌ Failed to update user profile assets:', error);
      throw error;
    }
  },

  // Get public ads (authorized endpoint)
  getPublicAds: async (pageNumber = 1, pageSize = 10, filters = {}, language = null) => {
    try {
      const endpoint = AUTH_ENDPOINTS.PUBLIC_ADS(pageNumber, pageSize, filters);
      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch public ads:', error);
      throw error;
    }
  },

  // Get post overview (for service-provider ad detail)
  getPostOverview: async (postId, language = null) => {
    try {
      const endpoint = AUTH_ENDPOINTS.POST_OVERVIEW(postId);
      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch post overview:', error);
      throw error;
    }
  },

  // Get admin post overview (for superadmin ad detail, no caching)
  getAdminPostOverview: async (postId, language = null) => {
    try {
      const endpoint = AUTH_ENDPOINTS.ADMIN_POST_OVERVIEW(postId);
      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch admin post overview:', error);
      throw error;
    }
  },

  // Get interested active ads (My Active Interests tab)
  getInterestedActiveAds: async (pageNumber = 1, pageSize = 10, filters = {}, language = null) => {
    try {
      const endpoint = AUTH_ENDPOINTS.INTERESTED_ACTIVE_ADS(pageNumber, pageSize, filters);
      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch interested active ads:', error);
      throw error;
    }
  },

  // Enroll service provider to a post (I'm interested)
  enrollServiceProvider: async (postId) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.POST_ENROLL_SERVICE_PROVIDER, { postId });
      return response;
    } catch (error) {
      console.error('Failed to enroll service provider:', error);
      throw error;
    }
  },

  // Dashboard Stats API
  getDashboardStats: async () => {
    try {
      const token = authenticatedApi.getAccessToken();
      const response = await apiService.get(AUTH_ENDPOINTS.DASHBOARD_STATS, token);
      return response;
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      throw error;
    }
  },
  // Get contractors (authenticated endpoint)
  getContractors: async (page = 1, pageSize = 10, status = null, filters = {}, sortTypes = []) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add filter parameters
      if (filters.search && filters.search.trim()) {
        // URLSearchParams.append() handles encoding automatically - don't double encode
        queryParams.append('Search', filters.search.trim());
      }
      if (filters.roles && filters.roles.trim()) {
        // Store roles separately to add manually (same approach as RequestedRoleFilter)
        const rolesValue = filters.roles.trim();
        queryParams._roles = rolesValue;
      }

      // Add sort parameters - single SortTypes parameter
      if (sortTypes && Array.isArray(sortTypes) && sortTypes.length > 0) {
        queryParams.append('SortTypes', sortTypes[0]); // Use only the first sort type
      }

      // Add status parameter if provided
      if (status && Array.isArray(status)) {
        status.forEach(s => {
          queryParams.append('Status', s);
        });
      } else if (status) {
        queryParams.append('Status', status);
      }

      // Use the base endpoint which already includes page and pageSize
      const baseEndpoint = AUTH_ENDPOINTS.CONTRACTORS(page, pageSize);
      let endpoint = baseEndpoint;

      // Add other query parameters
      if (queryParams.toString()) {
        endpoint += `&${queryParams.toString()}`;
      }

      // Manually add roles parameter (same approach as RequestedRoleFilter)
      if (queryParams._roles) {
        const separator = endpoint.includes('?') ? '&' : '?';
        endpoint += `${separator}Roles=${queryParams._roles}`;
      }



      const token = authenticatedApi.getAccessToken();
      const response = await apiService.get(endpoint, token);
      return response;
    } catch (error) {
      console.error('Failed to fetch contractors:', error);
      throw error;
    }
  },

  // Get service providers (authenticated endpoint)
  getServiceProviders: async (page = 1, pageSize = 10, status = null, filters = {}, sortTypes = [], language = null) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add filter parameters
      if (filters.search && filters.search.trim()) {
        // URLSearchParams.append() handles encoding automatically - don't double encode
        queryParams.append('Search', filters.search.trim());
      }
      if (filters.roles) {
        // Handle roles as array - append each role as a separate roles parameter
        if (Array.isArray(filters.roles)) {
          filters.roles.forEach(role => {
            if (role && role.trim()) {
              queryParams.append('roles', role.trim());
            }
          });
        } else if (typeof filters.roles === 'string' && filters.roles.trim()) {
          // If string, split by comma and add each as separate roles parameter
          const rolesArray = filters.roles.split(',').map(r => r.trim()).filter(Boolean);
          rolesArray.forEach(role => {
            queryParams.append('roles', role);
          });
        }
      }

      // Add sort parameters - single SortTypes parameter
      if (sortTypes && Array.isArray(sortTypes) && sortTypes.length > 0) {
        queryParams.append('SortTypes', sortTypes[0]); // Use only the first sort type
      }

      // Add status parameter if provided
      if (status && Array.isArray(status)) {
        status.forEach(s => {
          queryParams.append('status', s);
        });
      } else if (status) {
        queryParams.append('status', status);
      }

      // Use the base endpoint which already includes page and pageSize
      const baseEndpoint = AUTH_ENDPOINTS.SERVICE_PROVIDERS(page, pageSize);
      let endpoint = baseEndpoint;

      // Add all query parameters
      if (queryParams.toString()) {
        endpoint += `&${queryParams.toString()}`;
      }



      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch service providers:', error);
      throw error;
    }
  },

  // Get service providers by license (authenticated endpoint)
  getServiceProvidersByLicense: async (page = 1, pageSize = 10, statuses = []) => {
    try {
      const endpoint = AUTH_ENDPOINTS.SERVICE_PROVIDERS_BY_LICENSE(page, pageSize, statuses);
      const token = authenticatedApi.getAccessToken();
      const response = await apiService.get(endpoint, token);
      return response;
    } catch (error) {
      console.error('Failed to fetch service providers by license:', error);
      throw error;
    }
  },

  // Get customers (authenticated endpoint)
  getCustomers: async (page = 1, pageSize = 10, status = null, filters = {}, sortTypes = []) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add filter parameters
      if (filters.search && filters.search.trim()) {
        const searchTerm = filters.search.trim();

        // Helper function to detect if search term is email or phone number
        const isEmailOrPhone = (term) => {
          // Email pattern: contains @ and has valid email structure
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          // Phone pattern: contains + or digits with potential spaces, dashes, parentheses
          const phonePattern = /^[\+]?[0-9\s\-\(\)]{7,}$/;

          return emailPattern.test(term) || phonePattern.test(term);
        };

        // Always use URLSearchParams.append() which handles encoding automatically
        // This prevents double encoding issues
        queryParams.append('Search', searchTerm);
      }

      // Add sort parameters - single SortTypes parameter
      if (sortTypes && Array.isArray(sortTypes) && sortTypes.length > 0) {
        queryParams.append('SortTypes', sortTypes[0]); // Use only the first sort type
      }

      // Add status parameter if provided
      if (status && Array.isArray(status)) {
        status.forEach(s => {
          queryParams.append('Status', s);
        });
      } else if (status) {
        queryParams.append('Status', status);
      }

      // Use the base endpoint which already includes page and pageSize
      const baseEndpoint = AUTH_ENDPOINTS.CUSTOMERS(page, pageSize);
      const endpoint = queryParams.toString() ? `${baseEndpoint}&${queryParams.toString()}` : baseEndpoint;

      const response = await authApiService.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      throw error;
    }
  },

  // Get customer details (authenticated endpoint)
  getCustomerDetails: async (id) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.CUSTOMER_DETAILS(id));
      return response;
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      throw error;
    }
  },

  // Get all users (contractors and engineers) for approvals (authenticated endpoint)
  getAllUsers: async (page = 1, pageSize = 10) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.ALL_USERS(page, pageSize));
      return response;
    } catch (error) {
      console.error('Failed to fetch all users:', error);
      throw error;
    }
  },

  getQuotations: async (statuses = ['CREATED', 'DECLINED', 'INPROGRESS'], page = 1, pageSize = 5, filters = {}) => {
    try {
      const token = authenticatedApi.getAccessToken();



      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add status parameters (multiple Status=value)
      statuses.forEach(status => {
        queryParams.append('Status', status);
      });

      // Add pagination parameters
      queryParams.append('Page', page.toString());
      queryParams.append('PageSize', pageSize.toString());

      // Add filter parameters
      if (filters.search && filters.search.trim()) {
        // URLSearchParams.append() handles encoding automatically - don't double encode
        queryParams.append('Search', filters.search.trim());
      }
      if (filters.status && filters.status.trim()) {
        queryParams.append('FilterStatus', filters.status.trim());
      }
      if (filters.type && filters.type.trim() && filters.type !== 'all') {
        // Map human-readable filter values to API values (lowercase, no spaces for RequestedRoleFilter)
        const apiTypeMap = {
          'Main Contractor': 'maincontractor',
          'Sub Contractor': 'subcontractor',
          'Engineer': 'engineer'
        };
        const apiType = apiTypeMap[filters.type.trim()] || filters.type.trim().toLowerCase().replace(/\s+/g, '');
        queryParams.append('RequestedRoleFilter', apiType);
      }

      // Add sort parameters
      if (filters.sortTypes && Array.isArray(filters.sortTypes) && filters.sortTypes.length > 0) {
        filters.sortTypes.forEach(sortType => {
          queryParams.append('SortTypes', sortType);
        });
      }

      // Note: requestedRole parameter removed - only RequestedRoleFilter should be used

      const endpoint = `${AUTH_ENDPOINTS.GET_QUOTATIONS}?${queryParams.toString()}`;


      const response = await apiService.get(endpoint, token);


      // Ensure we return the data properly
      if (response && typeof response === 'object') {
        return response;
      } else {

        return response;
      }
    } catch (error) {
      console.error('Failed to fetch quotations:', error);
      throw error;
    }
  },

  submitQuotation: async (quotationData) => {
    try {
      const token = authenticatedApi.getAccessToken();

      const response = await apiService.post(AUTH_ENDPOINTS.SUBMIT_QUOTATION, quotationData, token);
      return response;
    } catch (error) {
      console.error('❌ API Service: Failed to submit quotation:', error);
      console.error('❌ API Service: Error details:', {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });
      throw error;
    }
  },

  // Change quotation status (accept/decline)
  changeQuotationStatus: async (payload) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.CHANGE_QUOTATION_STATUS, payload);
      return response;
    } catch (error) {
      console.error('Failed to change quotation status:', error);
      throw error;
    }
  },

  requestQuotationReschedule: async (payload) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.REQUEST_QUOTATION_RESCHEDULE, payload);
      return response;
    } catch (error) {
      console.error('Failed to request quotation reschedule:', error);
      throw error;
    }
  },

  // Get all quotations for admin
  getAllQuotations: async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.ALL_QUOTATIONS(page, pageSize, filters));
      return response;
    } catch (error) {
      console.error('Failed to fetch all quotations:', error);
      throw error;
    }
  },

  // Update user availability
  updateAvailability: async (isAvailable) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.UPDATE_AVAILABILITY(isAvailable));
      return response;
    } catch (error) {
      console.error('Failed to update availability:', error);
      throw error;
    }
  },

  getQuotationOverview: async (id) => {
    try {
      const token = authenticatedApi.getAccessToken();



      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiService.get(AUTH_ENDPOINTS.QUOTATION_OVERVIEW(id), token);


      return response;
    } catch (error) {
      console.error('Failed to fetch quotation overview:', error);
      throw error;
    }
  },

  getQuotationDetail: async (id) => {
    try {
      const token = authenticatedApi.getAccessToken();


      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiService.get(AUTH_ENDPOINTS.QUOTATION_DETAIL(id), token);


      return response;
    } catch (error) {
      console.error('Failed to fetch quotation detail:', error);
      throw error;
    }
  },

  getQuotationTimeline: async (quotationId) => {
    try {
      const token = authenticatedApi.getAccessToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await apiService.get(AUTH_ENDPOINTS.QUOTATION_TIMELINE(quotationId), token);


      return response;
    } catch (error) {
      console.error('Failed to fetch quotation timeline:', error);
      throw error;
    }
  },
  getContractorDetails: async (id) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.CONTRACTOR_DETAILS(id));
      return response;
    } catch (error) {
      console.error('Failed to fetch contractor details:', error);
      throw error;
    }
  },

  // Get service provider quotations (authenticated endpoint)
  getServiceProviderQuotations: async (serviceProviderId, page = 1, pageSize = 10, status = null, search = null, dateFrom = null, dateTo = null, type = null, sortTypes = []) => {
    try {
      const token = authenticatedApi.getAccessToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      queryParams.append('Page', page.toString());
      queryParams.append('PageSize', pageSize.toString());

      // Add status filter if provided
      // Handle both single status string and array of statuses
      if (status) {
        if (Array.isArray(status)) {
          // If status is an array, append each status as a separate Status parameter
          status.forEach(s => {
            queryParams.append('Status', s);
          });
        } else {
          // If status is a single string, append it once
          queryParams.append('Status', status);
        }
      }

      // Add search filter if provided
      if (search && search.trim()) {
        queryParams.append('Search', search.trim());
      }

      // Add date filters if provided
      if (dateFrom) {
        queryParams.append('FromDate', dateFrom);
      }
      if (dateTo) {
        queryParams.append('ToDate', dateTo);
      }

      // Add type filter if provided (only if not 'all')
      if (type && type !== 'all') {
        // Map human-readable filter values to API values (lowercase, no spaces for RequestedRoleFilter)
        const apiTypeMap = {
          'Main Contractor': 'maincontractor',
          'Sub Contractor': 'subcontractor',
          'Engineer': 'engineer'
        };
        const apiType = apiTypeMap[type] || type.toLowerCase().replace(/\s+/g, '');
        queryParams.append('RequestedRoleFilter', apiType);
      }

      // Add sorting parameters if provided
      if (sortTypes && sortTypes.length > 0) {
        sortTypes.forEach(sortType => {
          queryParams.append('SortTypes', sortType);
        });
      }

      const endpoint = `${AUTH_ENDPOINTS.SERVICE_PROVIDER_QUOTATIONS(serviceProviderId)}&${queryParams.toString()}`;


      const response = await apiService.get(endpoint, token);


      return response;
    } catch (error) {
      console.error('Failed to fetch service provider quotations:', error);
      throw error;
    }
  },

  // Get service provider quotation overview (authenticated endpoint)
  getServiceProviderQuotationOverview: async (quotationId) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.SERVICE_PROVIDER_QUOTATION_OVERVIEW(quotationId));
      return response;
    } catch (error) {
      console.error('Failed to fetch service provider quotation overview:', error);
      throw error;
    }
  },

  // Get customer quotations (authenticated endpoint)
  getCustomerQuotations: async (customerId, page = 1, pageSize = 10, status = null, search = null, dateFrom = null, dateTo = null, type = null, sortTypes = []) => {
    try {
      const token = authenticatedApi.getAccessToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      queryParams.append('Page', page.toString());
      queryParams.append('PageSize', pageSize.toString());

      // Add status filter if provided
      if (status) {
        queryParams.append('Status', status);
      }

      // Add search filter if provided
      if (search && search.trim()) {
        queryParams.append('Search', search.trim());
      }

      // Add date filters if provided
      if (dateFrom) {
        queryParams.append('FromDate', dateFrom);
      }
      if (dateTo) {
        queryParams.append('ToDate', dateTo);
      }

      // Add type filter if provided (only if not 'all')
      if (type && type !== 'all') {
        // Map human-readable filter values to API values (lowercase, no spaces for RequestedRoleFilter)
        const apiTypeMap = {
          'Main Contractor': 'maincontractor',
          'Sub Contractor': 'subcontractor',
          'Engineer': 'engineer'
        };
        const apiType = apiTypeMap[type] || type.toLowerCase().replace(/\s+/g, '');
        queryParams.append('RequestedRoleFilter', apiType);
      }

      // Add sorting parameters if provided
      if (sortTypes && sortTypes.length > 0) {
        sortTypes.forEach(sortType => {
          queryParams.append('SortTypes', sortType);
        });
      }

      const endpoint = `${AUTH_ENDPOINTS.CUSTOMER_QUOTATIONS(customerId)}&${queryParams.toString()}`;

      const response = await apiService.get(endpoint, token);

      return response;
    } catch (error) {
      console.error('Failed to fetch customer quotations:', error);
      throw error;
    }
  },

  // change contractor status (authenticated endpoint)
  approveContractor: async (userId, approvalStatus, rejectedReason = null) => {
    try {
      const requestData = { userId, approvalStatus };

      // Add rejectedReason if status is REJECTED or DISABLED
      if (approvalStatus === 'REJECTED' || approvalStatus === 'DISABLED') {
        requestData.rejectedReason = rejectedReason;
      }


      const response = await authApiService.post(AUTH_ENDPOINTS.CONTRACTOR_APPROVAL, requestData);

      return response;
    } catch (error) {
      console.error('Failed to change contractor approval status:', error);
      throw error;
    }
  },
  // change contractor Lincense status (authenticated endpoint)
  approveLicense: async (serviceProviderId, status, RejectionReason = null) => {
    try {
      const requestData = { serviceProviderId, status };

      // Add rejectionReason if status is REJECTED
      if (status === 'REJECTED') {
        requestData.rejectionReason = RejectionReason;
      }


      const response = await authApiService.post(AUTH_ENDPOINTS.CONTRACTOR_LICENSE_APPROVAL, requestData);

      return response;
    } catch (error) {
      console.error('Failed to change contractor approval status:', error);
      throw error;
    }
  },
  // Request resubmission for rejected contractor
  requestResubmission: async (userId) => {
    try {
      const requestData = { userId };
      const response = await authApiService.post(AUTH_ENDPOINTS.REQUEST_RESUBMISSION, requestData);
      return response;
    } catch (error) {
      console.error('Failed to request resubmission:', error);
      throw error;
    }
  },
  getServices: async () => {
    try {
      const response = await publicApiService.get(PUBLIC_ENDPOINTS.SERVICES_OFFERED);
      return response;
    } catch (error) {
      console.error('Failed to fetch services:', error);
      throw error;
    }
  },

  // Questionnaire: Get form questions (authenticated endpoint)
  getQuestionnaireFormQuestions: async (language = null, propertyType = null) => {
    try {
      let endpoint = AUTH_ENDPOINTS.QUESTIONNAIRE_FORM_QUESTIONS;
      if (propertyType) {
        endpoint = `${endpoint}?propertyType=${encodeURIComponent(propertyType)}`;
      }
      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch questionnaire form questions:', error);
      throw error;
    }
  },

  // Create post (authenticated endpoint)
  createPost: async (payload, language = null) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.POSTS_CREATE, payload, language);
      return response;
    } catch (error) {
      console.error('Failed to create post:', error);
      throw error;
    }
  },

  // Update post (authenticated endpoint) - PUT /Admin/posts/{postId}
  updatePost: async (postId, payload, language = null) => {
    try {
      const response = await authApiService.put(AUTH_ENDPOINTS.ADMIN_POST_UPDATE(postId), payload, language);
      return response;
    } catch (error) {
      console.error('Failed to update post:', error);
      throw error;
    }
  },

  // Property: get admin user properties (authenticated endpoint)
  getPropertyAdminUser: async (userId, language = null) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.PROPERTY_ADMIN_USER(userId), language);
      return response;
    } catch (error) {
      console.error('Failed to fetch property admin user:', error);
      throw error;
    }
  },

  // Property: create (authenticated endpoint)
  createProperty: async (payload, language = null) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.PROPERTIES_CREATE, payload, language);
      return response;
    } catch (error) {
      console.error('Failed to create property:', error);
      throw error;
    }
  },

  // Property: update (authenticated endpoint)
  updateProperty: async (payload, language = null) => {
    try {
      const response = await authApiService.put(AUTH_ENDPOINTS.PROPERTIES_UPDATE, payload, language);
      return response;
    } catch (error) {
      console.error('Failed to update property:', error);
      throw error;
    }
  },

  setPropertyDefault: async (propertyId, language = null) => {
    try {
      const response = await authApiService.put(AUTH_ENDPOINTS.PROPERTY_SET_DEFAULT, { propertyId }, language);
      return response;
    } catch (error) {
      console.error('Failed to set default property:', error);
      throw error;
    }
  },

  // Admin posts list (authenticated endpoint)
  getAdminPosts: async (pageNumber = 1, pageSize = 10, roleTypeId = "", locationId = "", language = null, sortBy = "", filterStatus = "") => {
    try {
      const response = await authApiService.get(
        AUTH_ENDPOINTS.ADMIN_POSTS_GET(pageNumber, pageSize, roleTypeId, locationId, sortBy, filterStatus),
        language
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch admin posts:', error);
      throw error;
    }
  },

  // File upload services
  requestPresignedUrls: async (files) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.PRESIGNED_URLS, files);
      return response;
    } catch (error) {
      console.error('Failed to request presigned URLs:', error);
      throw error;
    }
  },

  // Get user profile
  getUserProfile: async () => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.USER_PROFILE);
      return response;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.UPDATE_PROFILE, profileData);
      return response;
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw error;
    }
  },

  // Get locations (countries and states)
  getLocations: async () => {
    try {
      const response = await publicApi.get(PUBLIC_ENDPOINTS.GET_LOCATIONS);
      return response;
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      throw error;
    }
  },

  // Get supplier materials hierarchy
  getSupplierMaterials: async () => {
    const response = await authApiService.get('/Users/supplier/materials');
    return response;
  },

  // Update supplier materials (add/remove)
  updateSupplierMaterials: async ({ addMaterialIds = [], removeMaterialIds = [] }) => {
    const response = await authApiService.put('/Users/supplier/materials', { addMaterialIds, removeMaterialIds });
    return response;
  },

  // Update user language preference
  updateLanguage: async (language) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.UPDATE_LANGUAGE, { language });
      return response;
    } catch (error) {
      console.error('Failed to update language:', error);
      throw error;
    }
  },

  // Get all notifications
  getAllNotifications: async () => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.GET_ALL_NOTIFICATIONS);
      return response;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      throw error;
    }
  },

  // Mark notifications as read
  setReadAll: async (notificationIds) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.SET_READ_ALL, { notificationIds });
      return response;
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      throw error;
    }
  },

  // Get unread notifications count
  getUnreadCount: async () => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.GET_UNREAD_COUNT);
      return response;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      throw error;
    }
  },

  // Top Service Providers
  getTopServiceProviders: async (userTypeId, language = null) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.TOP_SERVICE_PROVIDERS(userTypeId), language);
      return response;
    } catch (error) {
      console.error('Failed to fetch top service providers:', error);
      throw error;
    }
  },

  saveTopServiceProviders: async (data, language = null) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.SAVE_TOP_SERVICE_PROVIDERS, data, language);
      return response;
    } catch (error) {
      console.error('Failed to save top service providers:', error);
      throw error;
    }
  },

  // Offers management
  getOffers: async (searchTerm = null, filter = null, page = 1, pageSize = 10, language = null) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        params.append('searchTerm', searchTerm.trim());
      }
      if (filter && filter !== 'all') {
        params.append('filter', filter);
      }
      params.append('pageNumber', page.toString());
      params.append('pageSize', pageSize.toString());
      
      const queryString = params.toString();
      const endpoint = `${AUTH_ENDPOINTS.GET_OFFERS}?${queryString}`;
      
      const response = await authApiService.get(endpoint, language);
      return response;
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      throw error;
    }
  },

  getOffer: async (id) => {
    try {
      const response = await authApiService.get(AUTH_ENDPOINTS.GET_OFFER(id));
      return response;
    } catch (error) {
      console.error('❌ Failed to fetch offer:', error);
      throw error;
    }
  },

  createOffer: async (offerData, language = null) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.CREATE_OFFER, offerData, language);
      return response;
    } catch (error) {
      console.error('Failed to create offer:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });
      throw error;
    }
  },

  updateOffer: async (id, offerData, language = null) => {
    try {
      // PUT /api/Offers with new payload structure including offerRecords
      // The offerData already contains offerId from OfferModal, but we use the id parameter if offerId is missing
      const updatePayload = {
        offerId: offerData.offerId || id, // Use offerId from offerData or fallback to id parameter
        title: offerData.title || '',
        status: offerData.status || 'DISABLED',
        isFeatured: true,
        isVerified: true,
        isPaid: true,
        offerRecords: offerData.offerRecords || [
          {
            languageCode: 'en',
            offerImageUrl: offerData.offerImageUrlEn || offerData.offerImageUrl || '',
            redirectLink: offerData.redirectLinkEn || offerData.redirectLink || '',
          },
          {
            languageCode: 'ar',
            offerImageUrl: offerData.offerImageUrlAr || '',
            redirectLink: offerData.redirectLinkAr || '',
          },
        ],
      };
      const response = await authApiService.put(AUTH_ENDPOINTS.UPDATE_OFFER, updatePayload, language);
      return response;
    } catch (error) {
      console.error('❌ Failed to update offer:', error);
      throw error;
    }
  },

  deleteOffer: async (id, language = null) => {
    try {
      const response = await authApiService.delete(AUTH_ENDPOINTS.DELETE_OFFER(id), language);
      return response;
    } catch (error) {
      console.error('❌ Failed to delete offer:', error);
      throw error;
    }
  },

  reorderOffers: async (offerIds, language = null) => {
    try {
      const response = await authApiService.post(AUTH_ENDPOINTS.REORDER_OFFERS, { offerIds }, language);
      return response;
    } catch (error) {
      console.error('Failed to reorder offers:', error);
      throw error;
    }
  },

  updateOfferStatus: async (id, status, offerData = null, language = null) => {
    try {
      // Use PUT /api/Offers with new payload structure including offerRecords
      // If offerData is provided, use it; otherwise construct minimal payload
      let updatePayload;
      
      if (offerData) {
        // Use new payload structure with offerRecords
        updatePayload = {
          offerId: offerData.offerId || id,
          title: offerData.title || '',
          status: status, // Update only the status
          isFeatured: true,
          isVerified: true,
          isPaid: true,
          offerRecords: offerData.offerRecords || (() => {
            // If offerRecords doesn't exist, construct from legacy fields or empty records
            const enRecord = {
              languageCode: 'en',
              offerImageUrl: offerData.offerImageUrlEn || offerData.offerImageUrl || '',
              redirectLink: offerData.redirectLinkEn || offerData.redirectLink || '',
            };
            const arRecord = {
              languageCode: 'ar',
              offerImageUrl: offerData.offerImageUrlAr || '',
              redirectLink: offerData.redirectLinkAr || '',
            };
            return [enRecord, arRecord].filter(r => r.offerImageUrl || r.redirectLink);
          })(),
        };
      } else {
        // Minimal payload with just offerId and status (shouldn't happen in practice)
        updatePayload = {
          offerId: id,
          status: status,
          isFeatured: true,
          isVerified: true,
          isPaid: true,
          offerRecords: [],
        };
      }

      const response = await authApiService.put(AUTH_ENDPOINTS.UPDATE_OFFER, updatePayload, language);
      return response;
    } catch (error) {
      console.error('❌ Failed to update offer status:', error);
      throw error;
    }
  },

  saveOfferOrder: async (offers, language = null) => {
    try {
      // Format: { offers: [{ offerId: 0, orderId: 0 }] }
      const payload = {
        offers: offers.map((offer, index) => ({
          offerId: offer.offerId || 0,
          orderId: offer.orderId || offer.order || offer.priority || offer.displayOrder || index + 1,
        })),
      };
      const response = await authApiService.post(AUTH_ENDPOINTS.SAVE_OFFER_ORDER, payload, language);
      return response;
    } catch (error) {
      console.error('❌ Failed to save offer order:', error);
      throw error;
    }
  },

  // Material Master API methods
  getMaterialHierarchy: async (language = null, searchKey = null) => {
    const url = searchKey
      ? `${AUTH_ENDPOINTS.GET_MATERIAL_HIERARCHY}?searchKey=${encodeURIComponent(searchKey)}`
      : AUTH_ENDPOINTS.GET_MATERIAL_HIERARCHY;
    return authApiService.get(url, language);
  },

  getMaterial: async (materialId, language = null) => {
    return authApiService.get(AUTH_ENDPOINTS.GET_MATERIAL_BY_ID(materialId), language);
  },

  getSubCategoryById: async (id, language = null) => {
    return authApiService.get(AUTH_ENDPOINTS.GET_SUB_CATEGORY_BY_ID(id), language);
  },

  getMainCategories: async (language = null) => {
    return authApiService.get(AUTH_ENDPOINTS.GET_MAIN_CATEGORIES, language);
  },

  getSubCategories: async (mainCategoryId, language = null) => {
    const query = mainCategoryId ? `?mainCategoryId=${mainCategoryId}` : '';
    return authApiService.get(`${AUTH_ENDPOINTS.GET_SUB_CATEGORIES}${query}`, language);
  },

  // --- Helper: upload image to S3, return key ---
  // Pass the current access token explicitly so uploadSingleFile uses the
  // direct fetch path with its own 401 retry — avoiding the global
  // handleUnauthorized() in apiRequest which would redirect to login.
  _uploadImageToS3: async (imageFile) => {
    if (!imageFile) return null;
    const token = localStorage.getItem('access_token');
    const result = await uploadSingleFile(imageFile, FOLDER_TYPES.PROJECTS, null, token);
    console.log('🖼️ S3 Upload Result:', {
      success: result?.success,
      key: result?.key,
      url: result?.url || result?.fileUrl,
      fileName: result?.fileName,
      fullResult: result,
    });
    if (result?.success) return result.key;
    throw new Error('Failed to upload image to S3');
  },

  // --- Main Categories ---
  createMainCategory: async (data, language = null) => {
    const payload = { nameEn: data.nameEn, nameAr: data.nameAr };
    if (data.imageFile) {
      payload.imageUrl = await authenticatedApi._uploadImageToS3(data.imageFile);
    }
    return authApiService.post(AUTH_ENDPOINTS.CREATE_MAIN_CATEGORY, payload, language);
  },

  updateMainCategory: async (id, data, language = null) => {
    const payload = { nameEn: data.nameEn, nameAr: data.nameAr };
    if (data.imageFile) {
      payload.imageUrl = await authenticatedApi._uploadImageToS3(data.imageFile);
    } else if (data.existingImageUrl) {
      payload.imageUrl = data.existingImageUrl;
    }
    return authApiService.put(AUTH_ENDPOINTS.UPDATE_MAIN_CATEGORY(id), payload, language);
  },

  deleteMainCategory: async (id, language = null) => {
    return authApiService.delete(AUTH_ENDPOINTS.DELETE_MAIN_CATEGORY(id), language);
  },

  removeMainCategoryImage: async (id, language = null) => {
    return authApiService.patch(AUTH_ENDPOINTS.REMOVE_MAIN_CATEGORY_IMAGE(id), {}, language);
  },

  archiveMainCategory: async (id, language = null) => {
    return authApiService.patch(AUTH_ENDPOINTS.ARCHIVE_MAIN_CATEGORY(id), {}, language);
  },

  // --- Sub Categories ---
  createSubCategory: async (data, language = null) => {
    const payload = { nameEn: data.nameEn, nameAr: data.nameAr, mainCategoryId: data.mainCategoryId };
    if (data.imageFile) {
      payload.imageUrl = await authenticatedApi._uploadImageToS3(data.imageFile);
    }
    return authApiService.post(AUTH_ENDPOINTS.CREATE_SUB_CATEGORY, payload, language);
  },

  updateSubCategory: async (id, data, language = null) => {
    const payload = { nameEn: data.nameEn, nameAr: data.nameAr, mainCategoryId: data.mainCategoryId };
    if (data.imageFile) {
      payload.imageUrl = await authenticatedApi._uploadImageToS3(data.imageFile);
    } else if (data.existingImageUrl) {
      payload.imageUrl = data.existingImageUrl;
    }
    return authApiService.put(AUTH_ENDPOINTS.UPDATE_SUB_CATEGORY(id), payload, language);
  },

  deleteSubCategory: async (id, language = null) => {
    return authApiService.delete(AUTH_ENDPOINTS.DELETE_SUB_CATEGORY(id), language);
  },

  removeSubCategoryImage: async (id, language = null) => {
    return authApiService.patch(AUTH_ENDPOINTS.REMOVE_SUB_CATEGORY_IMAGE(id), {}, language);
  },

  archiveSubCategory: async (id, language = null) => {
    return authApiService.patch(AUTH_ENDPOINTS.ARCHIVE_SUB_CATEGORY(id), {}, language);
  },

  // --- Materials ---
  createMaterial: async (data, language = null) => {
    const payload = {
      nameEn: data.nameEn,
      nameAr: data.nameAr,
      mainCategoryId: data.mainCategoryId,
      subCategoryId: data.subCategoryId,
    };
    if (data.imageFile) {
      payload.imageUrl = await authenticatedApi._uploadImageToS3(data.imageFile);
    }
    if (data.sizes && data.sizes.length > 0) payload.sizes = data.sizes;
    // Brands for create: API expects { brandName, brandImageUrl }
    if (data.brands && data.brands.length > 0) {
      payload.brands = await Promise.all(data.brands.map(async (b) => {
        const brand = { brandName: b.brandName };
        if (b.brandImageUrl && b.brandImageUrl instanceof File) {
          brand.brandImageUrl = await authenticatedApi._uploadImageToS3(b.brandImageUrl);
        } else {
          brand.brandImageUrl = b.brandImageUrl || null;
        }
        return brand;
      }));
    }
    return authApiService.post(AUTH_ENDPOINTS.CREATE_MATERIAL, payload, language);
  },

  updateMaterial: async (id, data, language = null) => {
    const payload = {
      nameEn: data.nameEn,
      nameAr: data.nameAr,
      mainCategoryId: data.mainCategoryId,
      subCategoryId: data.subCategoryId,
    };
    if (data.imageFile) {
      payload.imageUrl = await authenticatedApi._uploadImageToS3(data.imageFile);
    } else if (data.existingImageUrl) {
      payload.imageUrl = data.existingImageUrl;
    }
    return authApiService.put(AUTH_ENDPOINTS.UPDATE_MATERIAL(id), payload, language);
  },

  deleteMaterial: async (id, language = null) => {
    return authApiService.delete(AUTH_ENDPOINTS.DELETE_MATERIAL(id), language);
  },

  removeMaterialImage: async (id, language = null) => {
    return authApiService.patch(AUTH_ENDPOINTS.REMOVE_MATERIAL_IMAGE(id), {}, language);
  },

  archiveMaterial: async (id, language = null) => {
    return authApiService.patch(AUTH_ENDPOINTS.ARCHIVE_MATERIAL(id), {}, language);
  },

  // --- Material Sizes ---
  createMaterialSize: async (materialId, data, language = null) => {
    return authApiService.post(AUTH_ENDPOINTS.CREATE_MATERIAL_SIZE(materialId), data, language);
  },

  updateMaterialSize: async (sizeId, data, language = null) => {
    return authApiService.put(AUTH_ENDPOINTS.UPDATE_MATERIAL_SIZE(sizeId), data, language);
  },

  deleteMaterialSize: async (sizeId, language = null) => {
    return authApiService.delete(AUTH_ENDPOINTS.DELETE_MATERIAL_SIZE(sizeId), language);
  },

  // --- Material Brands ---
  addMaterialBrand: async (materialId, data, language = null) => {
    const payload = { brandName: data.brandName };
    if (data.brandImageUrl && data.brandImageUrl instanceof File) {
      payload.brandImageUrl = await authenticatedApi._uploadImageToS3(data.brandImageUrl);
    } else if (data.brandImageUrl) {
      payload.brandImageUrl = data.brandImageUrl;
    }
    return authApiService.post(AUTH_ENDPOINTS.ADD_MATERIAL_BRAND(materialId), payload, language);
  },

  updateMaterialBrand: async (materialBrandId, data, language = null) => {
    const payload = { brandName: data.brandName };
    if (data.brandImageUrl && data.brandImageUrl instanceof File) {
      payload.brandImageUrl = await authenticatedApi._uploadImageToS3(data.brandImageUrl);
    } else if (data.brandImageUrl) {
      payload.brandImageUrl = data.brandImageUrl;
    }
    return authApiService.put(AUTH_ENDPOINTS.UPDATE_MATERIAL_BRAND(materialBrandId), payload, language);
  },

  deleteMaterialBrand: async (materialBrandId, language = null) => {
    return authApiService.delete(AUTH_ENDPOINTS.DELETE_MATERIAL_BRAND(materialBrandId), language);
  },
};

// ─── Supplier / Contractor Portal ─────────────────────────────────────────────

const _delay = (ms = 400) => new Promise((res) => setTimeout(res, ms));

const _mapProperty = (p) => ({
  // address dropdown fields
  id: p.propertyId,
  name: p.name,
  details: p.mapAddress || [p.street, p.city, p.location].filter(Boolean).join(', '),
  buildingName: p.buildingName ?? '',
  city: p.city ?? '',
  street: p.street ?? '',
  zipCode: p.zipCode ?? '',
  latitude: p.latitude ?? '',
  longitude: p.longitude ?? '',
  stateId: p.state?.id ?? 0,
  stateName: p.state?.name ?? '',
  isDefault: p.isDefault ?? false,
  // project card fields
  location: p.location || [p.city, p.state?.name].filter(Boolean).join(', ') || '',
  image: p.propertyCoverURL ?? '',
  supplierCount: (p.records_Group ?? []).reduce((sum, g) => sum + (g.quotationsCount ?? 0), 0),
});

// ── Mock data (replaced when real APIs are ready) ──────────────────────────


const _MOCK_MEDIA_FILES = [
  { id: 1, name: "Lorem file (optional).pdf", type: "pdf", url: "#" },
  { id: 2, name: "Lorem file (optional).jpeg", type: "image", url: "#" },
  { id: 3, name: "Lorem file (optional).jpeg", type: "image", url: "#" },
  { id: 4, name: "Lorem file (optional).pdf", type: "pdf", url: "#" },
  { id: 5, name: "Lorem file (optional).pdf", type: "pdf", url: "#" },
  { id: 6, name: "Lorem file (optional).jpeg", type: "image", url: "#" },
];

const _MOCK_RFQS = [
  { id: "REQ-16788", submittedDate: "12th July, 2025", materials: ["Nails, Bolts & Wires", "Woods"] },
  { id: "REQ-16789", submittedDate: "12th July, 2025", materials: ["Proofing", "Block Materials"] },
  { id: "REQ-16790", submittedDate: "12th July, 2025", materials: ["Blaster Materials", "General Hand Tools"] },
];





export const supplierApi = {
  /** GET /contractor/projects (mock) */
  getProjects: async ({ search = "" } = {}) => {
    await _delay();
    const q = search.toLowerCase().trim();
    const result = q
      ? _MOCK_PROJECTS.filter((p) => p.name.toLowerCase().includes(q) || p.location.toLowerCase().includes(q))
      : _MOCK_PROJECTS;
    return { success: true, result };
  },

  /** GET /api/Property/{propertyId} — LIVE */
  getProjectById: async (id) => {
    const response = await authApiService.get(`/Property/${id}`);
    const p = response?.result ?? response ?? {};
    return {
      success: true,
      result: {
        id: p.propertyId,
        name: p.propertyName ?? '',
        buildingName: p.buildingName ?? '',
        description: p.description ?? '',
        location: p.location ?? '',
        image: p.propertyCoverUrl ?? '',
        mainContractors: p.mainContractors ?? [],
        subContractors: p.subContractors ?? [],
        consultantDesign: p.consultantDesign ?? [],
        materialBookings: p.materialBookings ?? [],
        propertyFiles: p.propertyFiles ?? [],
        designRequirements: p.designRequirements ?? {},
      },
    };
  },

  /** GET /Property/get — LIVE */
  getProjectAddresses: async () => {
    const response = await authApiService.get('/Property/get');
    const properties = response?.result?.properties ?? [];
    return { success: true, result: properties.map(_mapProperty) };
  },

  /** POST /Property/create — LIVE */
  createProjectAddress: async (_projectId, data) => {
    const payload = {
      propertyId: 0,
      name: data.name ?? '',
      buildingName: data.buildingName ?? '',
      description: data.description ?? '',
      propertyCoverURL: data.propertyCoverURL ?? '',
      mapAddress: data.address ?? '',
      latitude: String(data.locationCoords?.lat ?? data.latitude ?? ''),
      longitude: String(data.locationCoords?.lng ?? data.longitude ?? ''),
      zipCode: data.zipCode ?? '',
      street: data.street ?? '',
      city: data.city ?? '',
      stateId: data.stateId ?? 0,
      propertyFiles: data.propertyFiles ?? [],
    };
    const response = await authApiService.post('/Property/create', payload);
    const propertyData = response?.result ?? response;
    return { success: true, result: _mapProperty(propertyData) };
  },

  /** GET /contractor/projects/:id/media (mock) */
  getProjectMedia: async (_projectId) => {
    await _delay();
    return { success: true, result: _MOCK_MEDIA_FILES };
  },

  /** GET /contractor/projects/:id/rfqs (mock) */
  getProjectRFQs: async (_projectId) => {
    await _delay();
    return { success: true, result: _MOCK_RFQS };
  },

  /** GET /AppHome/materials/hierarchy — LIVE */
  getMaterialCategories: async (language = null, searchKey = null) => {
    return authenticatedApi.getMaterialHierarchy(language, searchKey);
  },

  /** GET /Admin/materials/sub-categories?mainCategoryId=… — LIVE */
  getMaterialItems: async (categoryId, language = null) => {
    return authenticatedApi.getSubCategories(categoryId, language);
  },

  /** GET /ServiceProvider/get — LIVE (roleType=8 → suppliers) */
  getSuppliers: async ({ search = "", sort = "" } = {}) => {
    const params = new URLSearchParams();
    params.append('roleType', '8');
    params.append('pageNumber', '1');
    params.append('pageSize', '50');
    if (search.trim()) params.append('search', search.trim());
    if (sort === 'name') params.append('sort_type', 'name_asc');
    else if (sort === 'rating') params.append('sort_type', 'rating');
    const response = await authApiService.get(`/ServiceProvider/get?${params.toString()}`);
    return { success: true, result: response?.result?.serviceProviders ?? [] };
  },

  /** POST /api/Booking/property-materials — LIVE */
  createMaterialBooking: async (payload) => {
    const response = await authApiService.post('/Booking/property-materials', payload);
    return response;
  },

  /** GET /api/Quotation/supplier/requests — paginated list with optional filters */
  getSupplierRequests: async ({ page = 1, limit = 20, subCategoryId, status, sortBy } = {}) => {
    const params = new URLSearchParams();
    params.set('Page', page);
    params.set('Limit', limit);
    if (subCategoryId != null) params.set('SubCategoryId', subCategoryId);
    if (status != null) params.set('Status', status);
    if (sortBy != null) params.set('SortBy', sortBy);
    const response = await authApiService.get(`/Quotation/supplier/requests?${params.toString()}`);
    return response;
  },

  getSupplierRequestDetails: async (id) => {
    const response = await authApiService.get(`/quotation/supplier/requests/${id}`);
    return response;
  },

  acceptSupplierRequest: async (quotationRequestId) => {
    const response = await authApiService.post(`/Quotation/supplier/requests/${quotationRequestId}/accept`);
    return response;
  },

  rejectSupplierRequest: async (quotationRequestId, reason) => {
    const response = await authApiService.post(`/Quotation/supplier/requests/${quotationRequestId}/reject`, { reason: reason || '' });
    return response;
  },

  submitSupplierQuotation: async (quotationRequestId, body) => {
    const response = await authApiService.post(`/Quotation/supplier/requests/${quotationRequestId}/submit`, body);
    return response;
  },

  getQuotationProcessDetail: async (quotationRequestId) => {
    const response = await authApiService.get(`/Quotation/supplier/requests/${quotationRequestId}/process-detail`);
    return response?.result ?? response;
  },

  getBookingMaterialDetails: async (bookingId) => {
    const response = await authApiService.get(`/Booking/property-materials/${bookingId}`);
    return response?.result ?? response;
  },

  getContractorQuotationRequestDetail: async (quotationRequestId) => {
    const response = await authApiService.get(`/Booking/quotation-requests/${quotationRequestId}/detail`);
    return response?.result ?? response;
  },

  acceptQuotationRequest: async (quotationRequestId) => {
    const response = await authApiService.post(`/quotation/material-requests/${quotationRequestId}/approve`);
    return response?.result ?? response;
  },

  rejectQuotationRequest: async (quotationRequestId, reason) => {
    const response = await authApiService.post(`/quotation/material-requests/${quotationRequestId}/reject`, { reason: reason ?? "" });
    return response?.result ?? response;
  },

  negotiateQuotationRequest: async (quotationRequestId, body) => {
    const response = await authApiService.post(`/quotation/material-requests/${quotationRequestId}/negotiate`, body);
    return response?.result ?? response;
  },

  getQuotationHistory: async (quotationRequestId) => {
    const response = await authApiService.get(`/Quotation/material-requests/${quotationRequestId}/history`);
    return response?.result ?? response;
  },

  getPaymentProviders: async () => {
    const response = await authApiService.get('/Payment/providers');
    return response?.result ?? response;
  },

  checkoutMaterialQuotationRequest: async (quotationRequestId, provider) => {
    const q =
      provider != null && String(provider).trim() !== ''
        ? `?provider=${encodeURIComponent(String(provider).trim())}`
        : '';
    const response = await authApiService.post(`/Quotation/material-requests/${quotationRequestId}/checkout${q}`, {});
    return response?.result ?? response;
  },

  getBookingMaterialQuotationDetails: async (bookingId, supplierQuotationId) => {
    const response = await authApiService.get(`/Booking/property-materials/${bookingId}/quotations/${supplierQuotationId}`);
    return response?.result ?? response;
  },

  getMaterialSubCategories: async (language = null) => {
    const response = await authApiService.get('/AppHome/materials/sub-categories', language);
    return response;
  },

  getQuotationRequestStatuses: async () => {
    const response = await authApiService.get('/MetaData/quotation-request-statuses');
    return response;
  },

  getOrderIssueTypes: async () => {
    const response = await authApiService.get('/MetaData/order-issue-types');
    return response;
  },

  getSupplierRequestSortOptions: async () => {
    const response = await authApiService.get('/MetaData/supplier-quotation-request-sort-by');
    return response;
  },

  /** POST /api/Order/{orderId}/accept */
  acceptOrder: async (orderId) => {
    const response = await authApiService.post(`/Order/${orderId}/accept`, {});
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/confirm */
  confirmOrderDelivery: async (orderId) => {
    const response = await authApiService.post(`/Order/${orderId}/confirm`, {});
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/refund */
  refundOrder: async (orderId, reason) => {
    const response = await authApiService.post(`/Order/${orderId}/refund`, { reason });
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/cancel */
  cancelOrder: async (orderId, reason) => {
    const response = await authApiService.post(`/Order/${orderId}/cancel`, { reason });
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/status/in-progress */
  markOrderInProgress: async (orderId) => {
    const response = await authApiService.post(`/Order/${orderId}/status/in-progress`, {});
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/status/out-for-delivery */
  markOrderOutForDelivery: async (orderId, deliveryInfo) => {
    const response = await authApiService.post(`/Order/${orderId}/status/out-for-delivery`, deliveryInfo);
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/status/delivered */
  markOrderDelivered: async (orderId) => {
    const response = await authApiService.post(`/Order/${orderId}/status/delivered`, {});
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/delivery-proof */
  uploadOrderDeliveryProof: async (orderId, proofURL) => {
    const response = await authApiService.post(`/Order/${orderId}/delivery-proof`, { proofURL });
    return response?.result ?? response;
  },

  /** PUT /api/Order/{orderId}/delivery-info */
  updateOrderDeliveryInfo: async (orderId, deliveryInfo) => {
    const response = await authApiService.put(`/Order/${orderId}/delivery-info`, deliveryInfo);
    return response?.result ?? response;
  },

  /** GET /api/Order */
  getOrders: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.status)    q.set('Status', params.status);
    if (params.searchKey) q.set('SearchKey', params.searchKey);
    if (params.dateFrom)  q.set('DateFrom', params.dateFrom);
    if (params.dateTo)    q.set('DateTo', params.dateTo);
    if (params.page)      q.set('Page', params.page);
    if (params.pageSize)  q.set('PageSize', params.pageSize);
    const qs = q.toString() ? `?${q.toString()}` : '';
    const response = await authApiService.get(`/Order${qs}`);
    return response?.result ?? response;
  },

  /** GET /api/Order/{orderId} */
  getOrderDetail: async (orderId) => {
    const response = await authApiService.get(`/Order/${orderId}`);
    return response?.result ?? response;
  },

  /** GET /api/admin/orders */
  getAdminOrders: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.status)    q.set('status', params.status);
    if (params.searchKey) q.set('searchKey', params.searchKey);
    if (params.dateFrom)  q.set('dateFrom', params.dateFrom);
    if (params.dateTo)    q.set('dateTo', params.dateTo);
    if (params.page)      q.set('page', params.page);
    if (params.pageSize)  q.set('pageSize', params.pageSize);
    const qs = q.toString() ? `?${q.toString()}` : '';
    const response = await authApiService.get(`/admin/orders${qs}`);
    const result = response?.result ?? response;
    if (Array.isArray(result)) {
      return { items: result, totalPages: response?.totalPages ?? 1, totalCount: response?.totalCount ?? result.length };
    }
    return result;
  },

  /** GET /api/admin/orders/{orderId} */
  getAdminOrderDetail: async (orderId) => {
    const response = await authApiService.get(`/admin/orders/${orderId}`);
    return response?.result ?? response;
  },

  /** GET /api/Order/{orderId}/invoice */
  getOrderInvoice: async (orderId) => {
    const response = await authApiService.get(`/Order/${orderId}/invoice`);
    return response?.result ?? response;
  },

  /** POST /api/Order/{orderId}/issue */
  raiseOrderIssue: async (orderId, payload) => {
    const response = await authApiService.post(`/Order/${orderId}/issue`, payload);
    return response?.result ?? response;
  },

  /** GET /api/Order/{orderId}/issues */
  getOrderIssues: async (orderId) => {
    const response = await authApiService.get(`/Order/${orderId}/issues`);
    return response?.result ?? response;
  },

  /** POST /rfq (mock) */
  submitRFQ: async (_payload) => {
    await _delay(800);
    return { success: true, result: { rfqId: `REQ-${Math.floor(10000 + Math.random() * 90000)}` } };
  },

  /** POST /uploads (mock) */
  uploadFile: async (file) => {
    await _delay(1000);
    return { success: true, result: { id: `file-${Date.now()}`, name: file.name, url: URL.createObjectURL(file) } };
  },
};

export default publicApi; 
